/**
 * Hand-written formula tokenizer.
 *
 * The whole token stream is produced up front: formulas are short, and a
 * materialized array lets the Pratt parser peek freely without buffering
 * logic. Every token carries its source `offset` so parse errors can quote a
 * character position (Requirement 5.2).
 *
 * Reference-shaped words (`A1`, `$B$7`, `Sheet1!A1`, `'My Sheet'!$A$1`) are
 * recognized here rather than in the parser, because deciding whether a word is
 * a reference or a Named_Range needs the raw characters.
 */

import { colToLabel, printSheetName, tryLabelToCol } from '../a1.js';
import { ERROR_CODES, MAX_COLS, MAX_ROWS, type ErrorCode } from '../types.js';
import type { RefNode } from './ast.js';

export type TokenKind =
  | 'num'
  | 'str'
  | 'bool'
  | 'err'
  | 'ref'
  | 'name'
  | 'op'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'colon'
  | 'eof';

interface TokenSpan {
  /** Offset of the first character of the token. */
  offset: number;
  /** Offset one past the last character of the token. */
  end: number;
  /** Raw source text of the token (empty for `eof`). */
  text: string;
}

export type Token = TokenSpan &
  (
    | { kind: 'num'; value: number }
    | { kind: 'str'; value: string }
    | { kind: 'bool'; value: boolean }
    | { kind: 'err'; value: ErrorCode }
    | { kind: 'ref'; ref: RefNode }
    | { kind: 'name'; name: string }
    | { kind: 'op'; op: string }
    | { kind: 'lparen' }
    | { kind: 'rparen' }
    | { kind: 'comma' }
    | { kind: 'colon' }
    | { kind: 'eof' }
  );

/** A tokenizer failure, shaped like the `ParseErr` fields it feeds. */
export interface LexError {
  offset: number;
  expected: string;
  found: string;
}

export type LexResult = { ok: true; tokens: Token[] } | { ok: false; error: LexError };

/** Operators, longest first so that `<=` wins over `<`. */
const OPERATORS: readonly string[] = ['<>', '<=', '>=', '<', '>', '=', '&', '+', '-', '*', '/', '^', '%'];

/** Error literals, longest first so `#NAME?` is not shadowed by a prefix. */
const ERROR_LITERALS: readonly ErrorCode[] = [...ERROR_CODES].sort((a, b) => b.length - a.length);

/** A word that is meant to be a cell reference, bounds not yet checked. */
const REF_SHAPE = /^(\$?)([A-Za-z]{1,3})(\$?)([0-9]+)$/;

/** A Named_Range: an identifier that is not reference-shaped. */
const NAME_SHAPE = /^[A-Za-z_][A-Za-z0-9_.]*$/;

const MAX_COL_LABEL = colToLabel(MAX_COLS - 1);

/**
 * True when a word may appear unquoted as a sheet prefix. Defined by
 * `printSheetName`, so the set of names the lexer accepts bare is exactly the
 * set the printer emits bare.
 */
function isBareSheetName(word: string): boolean {
  return printSheetName(word) === word;
}

export function tokenize(src: string): LexResult {
  const tokens: Token[] = [];
  let i = 0;

  while (i < src.length) {
    const ch = src.charAt(i);

    if (isSpace(ch)) {
      i += 1;
      continue;
    }

    if (ch === '(' || ch === ')' || ch === ',' || ch === ':') {
      const kind = ch === '(' ? 'lparen' : ch === ')' ? 'rparen' : ch === ',' ? 'comma' : 'colon';
      tokens.push({ kind, offset: i, end: i + 1, text: ch });
      i += 1;
      continue;
    }

    if (isDigit(ch) || (ch === '.' && isDigit(src.charAt(i + 1)))) {
      const scanned = scanNumber(src, i);
      if (!scanned.ok) return scanned;
      tokens.push(scanned.token);
      i = scanned.token.end;
      continue;
    }

    if (ch === '"') {
      const scanned = scanString(src, i);
      if (!scanned.ok) return scanned;
      tokens.push(scanned.token);
      i = scanned.token.end;
      continue;
    }

    if (ch === '#') {
      const scanned = scanErrorLiteral(src, i);
      if (!scanned.ok) return scanned;
      tokens.push(scanned.token);
      i = scanned.token.end;
      continue;
    }

    if (ch === "'") {
      const scanned = scanQuotedSheetRef(src, i);
      if (!scanned.ok) return scanned;
      tokens.push(scanned.token);
      i = scanned.token.end;
      continue;
    }

    if (isWordStart(ch)) {
      const scanned = scanWord(src, i);
      if (!scanned.ok) return scanned;
      tokens.push(scanned.token);
      i = scanned.token.end;
      continue;
    }

    const op = OPERATORS.find((candidate) => src.startsWith(candidate, i));
    if (op !== undefined) {
      tokens.push({ kind: 'op', op, offset: i, end: i + op.length, text: op });
      i += op.length;
      continue;
    }

    return err(i, 'a value, a reference, or an operator', ch);
  }

  tokens.push({ kind: 'eof', offset: src.length, end: src.length, text: '' });
  return { ok: true, tokens };
}

/** Human-readable description of a token, for the `found` field of an error. */
export function describeToken(token: Token): string {
  return token.kind === 'eof' ? 'end of formula' : token.text;
}

type ScanResult = { ok: true; token: Token } | { ok: false; error: LexError };

function scanNumber(src: string, start: number): ScanResult {
  let i = start;
  while (isDigit(src.charAt(i))) i += 1;
  if (src.charAt(i) === '.') {
    i += 1;
    while (isDigit(src.charAt(i))) i += 1;
  }
  const expMark = src.charAt(i);
  if (expMark === 'e' || expMark === 'E') {
    let j = i + 1;
    const sign = src.charAt(j);
    if (sign === '+' || sign === '-') j += 1;
    if (isDigit(src.charAt(j))) {
      while (isDigit(src.charAt(j))) j += 1;
      i = j;
    }
  }
  const text = src.slice(start, i);
  const value = Number(text);
  if (!Number.isFinite(value)) return err(start, 'a finite number', text);
  return { ok: true, token: { kind: 'num', value, offset: start, end: i, text } };
}

function scanString(src: string, start: number): ScanResult {
  let i = start + 1;
  let value = '';
  for (;;) {
    if (i >= src.length) return err(start, 'a closing double quote', 'end of formula');
    const ch = src.charAt(i);
    if (ch === '"') {
      if (src.charAt(i + 1) === '"') {
        value += '"';
        i += 2;
        continue;
      }
      i += 1;
      break;
    }
    value += ch;
    i += 1;
  }
  return { ok: true, token: { kind: 'str', value, offset: start, end: i, text: src.slice(start, i) } };
}

function scanErrorLiteral(src: string, start: number): ScanResult {
  const rest = src.slice(start).toUpperCase();
  const literal = ERROR_LITERALS.find((code) => rest.startsWith(code));
  if (literal === undefined) {
    return err(start, 'an error literal such as #REF!', src.slice(start, start + 1));
  }
  const end = start + literal.length;
  return {
    ok: true,
    token: { kind: 'err', value: literal, offset: start, end, text: src.slice(start, end) },
  };
}

/** Scans `'Sheet name'!A1`, unescaping `''` inside the sheet name. */
function scanQuotedSheetRef(src: string, start: number): ScanResult {
  let i = start + 1;
  let sheet = '';
  for (;;) {
    if (i >= src.length) return err(start, 'a closing single quote', 'end of formula');
    const ch = src.charAt(i);
    if (ch === "'") {
      if (src.charAt(i + 1) === "'") {
        sheet += "'";
        i += 2;
        continue;
      }
      i += 1;
      break;
    }
    sheet += ch;
    i += 1;
  }
  if (sheet.length === 0) return err(start, 'a non-empty sheet name', "''");
  if (src.charAt(i) !== '!') {
    return err(i, "'!' after a quoted sheet name", i >= src.length ? 'end of formula' : src.charAt(i));
  }
  i += 1;
  const bodyStart = i;
  while (isWordChar(src.charAt(i))) i += 1;
  const body = src.slice(bodyStart, i);
  if (body.length === 0) {
    return err(bodyStart, 'a cell reference after the sheet name', bodyStart >= src.length ? 'end of formula' : src.charAt(bodyStart));
  }
  const ref = refFromShape(body, sheet);
  if (ref === null) return err(bodyStart, 'a cell reference after the sheet name', body);
  if (ref === 'out-of-range') return err(bodyStart, refBoundsMessage(), body);
  return { ok: true, token: { kind: 'ref', ref, offset: start, end: i, text: src.slice(start, i) } };
}

/**
 * Scans an unquoted word: a boolean literal, a reference, a sheet-qualified
 * reference, or a Named_Range.
 */
function scanWord(src: string, start: number): ScanResult {
  let i = start;
  while (isWordChar(src.charAt(i))) i += 1;
  const word = src.slice(start, i);

  if (src.charAt(i) === '!') {
    if (!isBareSheetName(word)) {
      return err(start, 'a sheet name, or a quoted sheet name', word);
    }
    const bodyStart = i + 1;
    let j = bodyStart;
    while (isWordChar(src.charAt(j))) j += 1;
    const body = src.slice(bodyStart, j);
    if (body.length === 0) {
      return err(
        bodyStart,
        'a cell reference after the sheet name',
        bodyStart >= src.length ? 'end of formula' : src.charAt(bodyStart),
      );
    }
    const ref = refFromShape(body, word);
    if (ref === null) return err(bodyStart, 'a cell reference after the sheet name', body);
    if (ref === 'out-of-range') return err(bodyStart, refBoundsMessage(), body);
    return { ok: true, token: { kind: 'ref', ref, offset: start, end: j, text: src.slice(start, j) } };
  }

  const upper = word.toUpperCase();
  if (upper === 'TRUE' || upper === 'FALSE') {
    return {
      ok: true,
      token: { kind: 'bool', value: upper === 'TRUE', offset: start, end: i, text: word },
    };
  }

  const ref = refFromShape(word, undefined);
  if (ref === 'out-of-range') return err(start, refBoundsMessage(), word);
  if (ref !== null) {
    return { ok: true, token: { kind: 'ref', ref, offset: start, end: i, text: word } };
  }

  if (NAME_SHAPE.test(word)) {
    return { ok: true, token: { kind: 'name', name: word, offset: start, end: i, text: word } };
  }
  return err(start, 'a cell reference or a name', word);
}

/**
 * Builds a `ref` node from a reference body.
 * `null` means "not reference-shaped"; `'out-of-range'` means it is shaped like
 * a reference but points outside the sheet bounds.
 */
function refFromShape(body: string, sheet: string | undefined): RefNode | null | 'out-of-range' {
  const m = REF_SHAPE.exec(body);
  if (m === null) return null;
  const col = tryLabelToCol(m[2] as string);
  const row1 = Number(m[4] as string);
  if (col === null || !Number.isInteger(row1) || row1 < 1 || row1 > MAX_ROWS) return 'out-of-range';
  const base: RefNode = {
    k: 'ref',
    absCol: m[1] === '$',
    col,
    absRow: m[3] === '$',
    row: row1 - 1,
  };
  return sheet === undefined ? base : { ...base, sheet };
}

function refBoundsMessage(): string {
  return `a cell reference within A1:${MAX_COL_LABEL}${MAX_ROWS}`;
}

function err(offset: number, expected: string, found: string): { ok: false; error: LexError } {
  return { ok: false, error: { offset, expected, found } };
}

function isSpace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f' || ch === '\v';
}

function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}

function isAlpha(ch: string): boolean {
  return (ch >= 'A' && ch <= 'Z') || (ch >= 'a' && ch <= 'z');
}

function isWordStart(ch: string): boolean {
  return isAlpha(ch) || ch === '_' || ch === '$';
}

function isWordChar(ch: string): boolean {
  return isAlpha(ch) || isDigit(ch) || ch === '_' || ch === '.' || ch === '$';
}
